<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { usePDF, VuePDF } from '@tato30/vue-pdf'
import 'pinch-zoom-element'

const props = defineProps({
  /** Dialog visibility (v-model) */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Array of media items to preview
   * Each item should have: { src: string, name?: string, type?: 'image' | 'pdf' }
   * If type is not provided, it will be auto-detected from src or name
   */
  files: {
    type: Array,
    default: () => [],
  },
  /** Initial index to display */
  initialIndex: {
    type: Number,
    default: 0,
  },
  /** Loading state */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Error message to display */
  error: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'change', 'prev', 'next'])

const { t } = useI18n()
const { mobile } = useDisplay()

// Dialog visibility
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Current file index
const currentIndex = ref(props.initialIndex)

// Current file
const currentFile = computed(() => {
  if (props.files.length === 0) return null
  return props.files[currentIndex.value] || props.files[0]
})

// Current file source
const fileSRC = computed(() => {
  return currentFile.value?.src || ''
})

// Detect file type (image or pdf)
const fileType = computed(() => {
  if (!currentFile.value) return 'image'
  
  // If type is explicitly provided
  if (currentFile.value.type) {
    return currentFile.value.type
  }
  
  // Auto-detect from name or src
  const fileName = currentFile.value.name || currentFile.value.src || ''
  const isPDF = fileName.toLowerCase().endsWith('.pdf')
  return isPDF ? 'pdf' : 'image'
})

// PDF handling - only pass source to usePDF when file type is PDF
// This prevents console warnings when previewing images
const pdfSRC = computed(() => {
  return fileType.value === 'pdf' ? fileSRC.value : ''
})
const { pdf, pages } = usePDF(pdfSRC)

// Image loading state
const isImageLoading = ref(true)

const onImageLoad = () => {
  isImageLoading.value = false
}

const onImageError = () => {
  isImageLoading.value = false
}

// Zoom controls
const ZOOM = ref(1)
const ZOOM_STEP = 0.1
const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const fitParent = ref(false) // Fit to width
const fitHeight = ref(true) // Fit to height - default view for images

/**
 * Zoom in
 */
const zoomIn = () => {
  if (fileType.value === 'image') {
    const pinchZoom = document.querySelector('.media-preview-pinch-zoom')
    if (!pinchZoom) return

    const currentScale = pinchZoom.scale || 1
    const newScale = Math.min(ZOOM_MAX, +(currentScale + ZOOM_STEP).toFixed(2))

    pinchZoom.setTransform({
      scale: newScale,
      allowChangeEvent: true,
    })

    ZOOM.value = newScale
  } else if (fileType.value === 'pdf') {
    fitParent.value = false
    ZOOM.value = Math.min(ZOOM_MAX, +(ZOOM.value + ZOOM_STEP).toFixed(2))
  }
}

/**
 * Zoom out
 */
const zoomOut = () => {
  if (fileType.value === 'image') {
    const pinchZoom = document.querySelector('.media-preview-pinch-zoom')
    if (!pinchZoom) return

    const currentScale = pinchZoom.scale || 1
    const newScale = Math.max(ZOOM_MIN, +(currentScale - ZOOM_STEP).toFixed(2))

    pinchZoom.setTransform({
      scale: newScale,
      allowChangeEvent: true,
    })

    ZOOM.value = newScale
  } else if (fileType.value === 'pdf') {
    fitParent.value = false
    ZOOM.value = Math.max(ZOOM_MIN, +(ZOOM.value - ZOOM_STEP).toFixed(2))
  }
}

/**
 * Reset zoom to default (no fit, original size)
 */
const resetZoom = () => {
  if (fileType.value === 'image') {
    const pinchZoom = document.querySelector('.media-preview-pinch-zoom')
    if (pinchZoom) {
      pinchZoom.setTransform({
        scale: 1,
        x: 0,
        y: 0,
        allowChangeEvent: true,
      })
    }
  }

  ZOOM.value = 1
  fitParent.value = false
  fitHeight.value = false
}

/**
 * Fit to width
 */
const fitToWidth = () => {
  if (fileType.value === 'image') {
    const pinchZoom = document.querySelector('.media-preview-pinch-zoom')
    if (pinchZoom) {
      pinchZoom.setTransform({
        scale: 1,
        x: 0,
        y: 0,
        allowChangeEvent: true,
      })
    }
  }

  ZOOM.value = 1
  fitParent.value = true
  fitHeight.value = false
}

/**
 * Fit to height - default view for images
 */
const fitToHeight = () => {
  if (fileType.value === 'image') {
    const pinchZoom = document.querySelector('.media-preview-pinch-zoom')
    if (pinchZoom) {
      pinchZoom.setTransform({
        scale: 1,
        x: 0,
        y: 0,
        allowChangeEvent: true,
      })
    }
  }

  ZOOM.value = 1
  fitParent.value = false
  fitHeight.value = true
}

/**
 * Handle pinch zoom change event
 */
const handlePinchZoomChange = (event) => {
  if (event.detail?.scale) {
    ZOOM.value = event.detail.scale
  }
}

/**
 * Go to previous file
 */
const prevFile = () => {
  if (currentIndex.value > 0) {
    // Reset to default fit-to-height view
    fitParent.value = false
    fitHeight.value = true
    ZOOM.value = 1
    if(fileSRC.value !== props.files[currentIndex.value-1]?.src ) isImageLoading.value = true // Reset loading state for new image when src changes
    currentIndex.value--
    emit('prev', currentIndex.value)
    emit('change', currentIndex.value)
  }
}

/**
 * Go to next file
 */
const nextFile = () => {
  if (currentIndex.value < props.files.length - 1) {
    // Reset to default fit-to-height view
    fitParent.value = false
    fitHeight.value = true
    ZOOM.value = 1
    if(fileSRC.value !== props.files[currentIndex.value+1]?.src ) isImageLoading.value = true // Reset loading state for new image when src changes
    currentIndex.value++
    emit('next', currentIndex.value)
    emit('change', currentIndex.value)
  }
}

/**
 * Close dialog
 */
const closeDialog = () => {
  isOpen.value = false
}

/**
 * Handle keyboard navigation
 */
const handleKeyboardNavigation = (event) => {
  if (!isOpen.value) return

  if (event.key === 'ArrowLeft' && currentIndex.value > 0) {
    prevFile()
  } else if (event.key === 'ArrowRight' && currentIndex.value < props.files.length - 1) {
    nextFile()
  } else if (event.key === 'Escape') {
    closeDialog()
  }
}

// Watch for initialIndex changes
watch(() => props.initialIndex, (newIndex) => {
  currentIndex.value = newIndex
  ZOOM.value = 1
  fitParent.value = false
  fitHeight.value = true // Default to fit-to-height
  isImageLoading.value = true // Reset loading state
})

// Watch for dialog open to reset zoom
watch(isOpen, (newValue) => {
  if (newValue) {
    currentIndex.value = props.initialIndex
    ZOOM.value = 1
    fitParent.value = false
    fitHeight.value = true // Default to fit-to-height
    isImageLoading.value = true // Reset loading state
  }
})

// Keyboard navigation
onMounted(() => {
  window.addEventListener('keydown', handleKeyboardNavigation)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyboardNavigation)
})

// Expose methods for programmatic control
defineExpose({
  prevFile,
  nextFile,
  zoomIn,
  zoomOut,
  resetZoom,
  fitToWidth,
  fitToHeight,
})
</script>

<template>
  <VDialog
    v-model="isOpen"
    :loading="loading"
    scrollable
    fullscreen
  >
    <VCard>
      <template #title>
        <div class="d-flex justify-space-between align-center pb-2">
          {{ $t('preview') }}
          <div class="d-flex align-center">
            <div class="preview-controls d-flex align-center mr-6">
              <VBtn
                icon
                size="small"
                variant="text"
                color="gray"
                :title="$t('reset_zoom')"
                @click="resetZoom"
              >
                <VIcon icon="ri-collapse-diagonal-2-line" />
              </VBtn>

              <VBtn
                icon
                size="small"
                variant="text"
                color="gray"
                :title="$t('fit_to_height')"
                @click="fitToHeight"
              >
                <VIcon icon="ri-expand-vertical-line" />
              </VBtn>

              <VBtn
                icon
                size="small"
                variant="text"
                color="gray"
                :title="$t('fit_to_width')"
                @click="fitToWidth"
              >
                <VIcon icon="ri-expand-horizontal-line" />
              </VBtn>

              <VBtn
                icon
                size="small"
                variant="text"
                color="gray"
                :disabled="ZOOM <= ZOOM_MIN"
                :title="$t('zoom_out')"
                @click="zoomOut"
              >
                <VIcon icon="ri-zoom-out-line" />
              </VBtn>

              <VBtn
                icon
                size="small"
                variant="text"
                color="gray"
                :disabled="ZOOM >= ZOOM_MAX"
                :title="$t('zoom_in')"
                @click="zoomIn"
              >
                <VIcon icon="ri-zoom-in-line" />
              </VBtn>
            </div>
            <VIcon
              icon="ri-close-line"
              :title="$t('$vuetify.close')"
              class="cursor-pointer"
              @click="closeDialog"
            />
          </div>
        </div>
      </template>

      <template #text>
        <div
          v-if="loading"
          class="d-flex align-center justify-center h-100"
        >
          <VProgressCircular indeterminate />
        </div>

        <div
          v-else
          class="position-relative h-100"
        >
          <!-- Image preview -->
          <div
            v-if="fileType === 'image'"
            class="media-preview-images h-100"
          >
            <!-- Image loading indicator -->
            <div
              v-if="isImageLoading"
              class="media-preview-loading"
            >
              <VProgressCircular
                indeterminate
                size="64"
                color="primary"
              />
            </div>
            
            <pinch-zoom
              v-show="!isImageLoading"
              class="media-preview-pinch-zoom"
              style="display: block; width: 100%; height: 100%"
              @change="handlePinchZoomChange"
            >
              <img
                alt="Podgląd pliku..."
                :src="fileSRC"
                :style="{
                  cursor: 'grab',
                  height: fitHeight ? '100%' : 'auto',
                  width: fitParent ? '100%' : 'auto',
                  maxWidth: fitHeight ? 'none' : '100%',
                  maxHeight: fitHeight ? 'calc(100vh - 120px)' : 'none',
                  display: 'block',
                  objectFit: fitHeight ? 'contain' : 'initial',
                }"
                @load="onImageLoad"
                @error="onImageError"
              >
            </pinch-zoom>
          </div>

          <!-- PDF preview -->
          <div
            v-if="fileType === 'pdf'"
            class="media-preview-pdf"
          >
            <VuePDF
              v-for="page in pages"
              :key="`${page}-${fitParent}`"
              :page="page"
              :pdf="pdf"
              :show-all="false"
              :scale="ZOOM"
              :fit-parent="fitParent"
            />
          </div>

          <!-- Title bar -->
          <div class="media-preview-title">
            <!-- Display potential error -->
            <div
              v-if="error"
              class="text-center text-h5 h-100 text-error"
            >
              {{ error }}
            </div>
            <div v-else-if="files.length > 0">
              [ {{ currentIndex + 1 }} / {{ files.length }} ]
              <template v-if="currentFile?.name">
                - {{ currentFile.name }}
              </template>
            </div>
          </div>

          <!-- Navigation arrows -->
          <div
            v-if="files.length > 1"
            class="media-preview-navigation"
          >
            <div
              class="nav-arrow pa-2 text-high-emphasis cursor-pointer"
              :style="{ opacity: currentIndex <= 0 ? 0.5 : 1 }"
              @click="prevFile"
            >
              <VIcon
                color="white"
                :size="mobile ? 'small' : 'x-large'"
                icon="ri-arrow-left-line"
              />
            </div>
            <div
              class="nav-arrow pa-2 text-high-emphasis cursor-pointer"
              :style="{ opacity: currentIndex >= files.length - 1 ? 0.5 : 1 }"
              @click="nextFile"
            >
              <VIcon
                color="white"
                :size="mobile ? 'small' : 'x-large'"
                icon="ri-arrow-right-line"
              />
            </div>
          </div>
        </div>
      </template>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
.media-preview-images,
.media-preview-pdf {
  touch-action: pan-x pan-y;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  canvas,
  img {
    margin: auto;
  }
}

.media-preview-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.media-preview-title {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: normal;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 12px;
  min-height: 50px;
  background: rgba(0, 0, 0, 0.69);
  padding: 0.5rem;
  text-align: center;
  color: white;
}

.media-preview-navigation {
  position: fixed;
  display: flex;
  justify-content: space-between;
  top: 50%;
  width: 95%;
  left: 0;
  right: 0;
  margin-inline: auto;

  .nav-arrow {
    background: rgba(0, 0, 0, 0.69);
    border-radius: 18px;
  }
}
</style>
